import { useEffect, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import CustomerType from "../../../components/modals/customers/create/CustomerType";
import PageTitle from "../../../components/partials/PageTitle";
import {
  FilteredIndividualCustomers,
  getIndividualCustomers,
  getIndividualCustomersSearch,
  resetCustomerStatus,
  resetSingleCustomer,
} from "../../../redux/slice/customer/customerSlice";
import { BeatLoader } from "react-spinners";
import moment from "moment/moment";
import pdf from "../../../assets/images/img-icons/pdf.png";
import excel from "../../../assets/images/img-icons/excel.png";
import { CSVLink } from "react-csv";
import { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import * as yup from "yup";
import Select from "../../../components/form/select/Select";
import { useFormik } from "formik";
import PaginationLinks from "../../../components/pagination/PaginationLinks";
import Loading from "../../../components/loader/Loading";

// sorting users by their offices to return only users in that office.
const sortUsersByOffice = (office_id, users) => {
  return users
    ?.filter((item) => parseInt(item?.office_id) === office_id)
    ?.map((item) => ({
      label: item?.last_name + " " + item?.first_name,
      value: item?.staff_id,
    }));
};

const AllCustomer = () => {
  const [createCorporateCustomer, setCreateCorporateCustomer] = useState();
  const [createIndividualCustomer, setCreateIndividualCustomer] = useState();
  const [hasSeeCustomer, setHasSeeCustomer] = useState();
  const [viewCustomer, setViewCustomer] = useState();

  const { user } = useSelector((state) => state?.auth?.user);
  const { user_permissions } = useSelector((state) => state?.auth?.user);
  const { all_users, unpaginated_users } = useSelector((state) => state?.user);
  const {
    offices: { offices },
  } = useSelector((state) => state.office);
  const { customers, isSuccess, isFiltered, isFiltering, isSearching } =
    useSelector((state) => state?.customer);
  const [currentCustomers, setCurrentCustomers] = useState(
    customers?.individual?.data
  );

  // local state annd refs
  const [sortedUsers, setSortedUsers] = useState([]);
  const [filtered, setFiltered] = useState("");
  const [filteredItem, setFilteredItem] = useState(false);
  const [searchBy, setSearchBy] = useState("");
  const [pageNumber, setPageNumber] = useState(1);

  // refs
  const agentCusRef = useRef();
  const individualOffFilter = useRef();
  const allIndividualCustomers = useRef();
  const ICPdfRef = useRef();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  // print table to pdf
  const generatePDF = useReactToPrint({
    content: () => ICPdfRef.current,
    documentTitle: "corperate customers",
  });

  // export data to excel file
  const exportXlsxData = () => {
    const itemsWithValues = {};
    return currentCustomers?.map((item) => {
      for (const key in item) {
        if (item[key]) {
          let keyChange = key?.split("_")?.join(" ");
          itemsWithValues[keyChange] = item[key];
        }
      }
      return itemsWithValues;
    });
  };

  // search for customer by anything related
  const handleSearchBy = (e) => {
    e.preventDefault();
    if (searchBy) {
      dispatch(
        getIndividualCustomersSearch({ searchBy, office_id: user?.office_id })
      );
    }
  };

  // fetch customers on click of pagination links
  const fetchIndividualCustomersHandler = async (pageNum) => {
    setPageNumber(pageNum);
    dispatch(
      getIndividualCustomers({
        pageNumber: pageNum,
        office_id: user?.office_id,
      })
    );
    dispatch(resetCustomerStatus());
  };

  // breadcrumb
  const breadcrumb = {
    from: {
      title: "Customer",
      link: "/admin/individual-customers",
    },
    current: {
      title: "overview",
      link: null,
    },
  };

  const {
    errors,
    values,
    touched,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldError,
    resetForm,
  } = useFormik({
    enableReinitialize: true,
    initialValues: {
      office_id: "",
      staff_id: "",
    },
    validationSchema: () =>
      yup.object().shape({
        office_id: yup.number().required("Office is required"),
      }),
    onSubmit: (values) => {
      if (values?.staff_id === undefined) {
        values["staff_id"] = "";
      }
      dispatch(resetCustomerStatus());
      dispatch(FilteredIndividualCustomers(values));

      // setFiltered("");
    },
  });

  // clear filter and search fields after request is completed.
  const clearFilteredItems = () => {
    individualOffFilter?.current?.clearValue();
    // agentCusRef?.current?.clearValue();
    setSearchBy("");
    dispatch(
      getIndividualCustomers({
        pageNumber: pageNumber,
        office_id: user?.office_id,
      })
    );
    resetForm();
    setFilteredItem(false);
  };

  useEffect(() => {
    dispatch(
      getIndividualCustomers({ pageNumber: 1, office_id: user?.office_id })
    );
    dispatch(resetSingleCustomer());
  }, []);

  useEffect(() => {
    if (customers) {
      setCurrentCustomers(customers?.individual?.data);
    }
  }, [customers]);

  useEffect(() => {
    agentCusRef?.current?.clearValue();
    if (values?.office_id) {
      let sortedUsers = sortUsersByOffice(values?.office_id, unpaginated_users);
      setSortedUsers(sortedUsers);
    } else {
      setSortedUsers([]);
    }
  }, [values?.office_id, unpaginated_users]);

  useEffect(() => {
    if (isSuccess) {
      allIndividualCustomers?.current?.click();
      setFiltered("");
      dispatch(resetCustomerStatus());
    }

    if (isFiltered) {
      setFilteredItem(true);
    }

    if (filtered !== "") {
      setCurrentCustomers(
        customers?.individual?.data?.filter((item) => item.status === filtered)
      );
    } else {
      setCurrentCustomers(customers?.individual?.data);
    }
  }, [filtered, isFiltered]);

  useEffect(() => {
    if (
      !user_permissions?.some(
        (permission) => permission.name === "customer module"
      ) ||
      !user_permissions?.some(
        (permission) => permission.name === "view customers - individual"
      )
    ) {
      navigate("/admin/dashboard");
    }

    setCreateIndividualCustomer(
      user_permissions?.some(
        (permission) => permission.name === "create customers - individual"
      )
    );
    setCreateCorporateCustomer(
      user_permissions?.some(
        (permission) => permission.name === "create customer - corporate"
      )
    );
    setViewCustomer(
      user_permissions?.some(
        (permission) => permission.name === "view customer details"
      )
    );
    setHasSeeCustomer(
      user_permissions?.some(
        (permission) => permission.name === "see customers"
      )
    );
  }, [navigate, user_permissions]);

  return (
    <>
      {/* page title */}
      <PageTitle title="Individual Customers" breadcrumb={breadcrumb} />
      {/* end page title */}

      {/* Table row */}

      {!customers?.individual && (
        <BeatLoader
          height="80"
          width="80"
          radius="9"
          color="#2e3192"
          ariaLabel="three-dots-loading"
          wrapperStyle={{}}
          wrapperClassName=""
          visible={true}
        />
      )}

      {customers?.individual && (
        <div className="row mt- card p-1">
          <div className="row mb-3">
            {hasSeeCustomer && (
              <form className="col-md-10" onSubmit={handleSubmit}>
                <div className="row mt-4">
                  <div className="col-sm-2 text-end sm-text-end">
                    <label htmlFor="account_type" className={`form-label mt-1`}>
                      Office <span className="text-danger">*</span>
                    </label>
                  </div>

                  <div className="col-sm-6">
                    <Select
                      onBlur={() => {
                        handleBlur({ target: { name: "office_id" } });
                      }}
                      ref={individualOffFilter}
                      name="office_id"
                      data={offices?.map((item, i) => ({
                        label: item?.office_name,
                        value: item?.id,
                      }))}
                      // value={parseInt(values?.office_id)}
                      onChange={(selectedOption) => {
                        setFieldValue("office_id", selectedOption?.value);
                      }}
                      isLoading={offices?.length < 1 && true}
                    />
                    {errors?.office_id && touched?.office_id && (
                      <p className="mb-0 text-danger">
                        {errors?.office_id + "*"}
                      </p>
                    )}
                  </div>
                </div>
                <div className="row mt-2">
                  <div className="col-sm-2 text-end sm-text-end">
                    <label htmlFor="account_type" className={`form-label mt-1`}>
                      Agent
                    </label>
                  </div>
                  <div className="col-sm-6">
                    <Select
                      onBlur={() => {
                        handleBlur({ target: { name: "staff_id" } });
                      }}
                      name="staff_id"
                      ref={agentCusRef}
                      data={
                        // values.office_id === 1
                        //   ? unpaginated_users?.map((item) => ({
                        //       label: item?.last_name + " " + item?.first_name,
                        //       value: item?.staff_id,
                        //     }))
                        //   :
                        sortedUsers
                      }
                      onChange={(selectedOption) => {
                        setFieldValue("staff_id", selectedOption?.value);
                        setFieldError("staff_id", "");
                      }}
                      isLoading={unpaginated_users.length > 0 ? false : true}
                    />
                    <div className="d-flex align-items-center gap-2 mt-2">
                      <button type="submit" className="btn btn-sm btn-primary ">
                        Filter
                      </button>
                      {filteredItem ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-secondary"
                          onClick={clearFilteredItems}
                        >
                          Clear Filter
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </form>
            )}
            {(createCorporateCustomer || createIndividualCustomer) && (
              <div className="col-md-2 pt-3">
                <div className="d-flex justify-content-end">
                  <button
                    className="btn btn-sm btn-primary mt-2 m-0"
                    data-bs-toggle="modal"
                    data-bs-target="#create_customer_step_2"
                  >
                    Add Customer
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="d-flex justify-content-between py-2">
            {/* <h4 className='text-primary-1'>All Customers</h4> */}
            <div>
              <ul class="nav nav-tabs nav-bordered mb-">
                <li class="nav-item" onClick={() => setFiltered("")}>
                  <a
                    href="#home-b1"
                    data-bs-toggle="tab"
                    aria-expanded="false"
                    class="nav-link active"
                    ref={allIndividualCustomers}
                  >
                    <i class="mdi mdi-home-variant d-md-none d-block"></i>
                    <span class="d-none d-md-block">All</span>
                  </a>
                </li>
                <li class="nav-item" onClick={() => setFiltered("2")}>
                  <a
                    href="#profile-b1"
                    data-bs-toggle="tab"
                    aria-expanded="true"
                    class="nav-link"
                  >
                    <i class="mdi mdi-account-circle d-md-none d-block"></i>
                    <span class="d-none d-md-block">Active</span>
                  </a>
                </li>
                <li class="nav-item" onClick={() => setFiltered("1")}>
                  <a
                    href="#settings-b1"
                    data-bs-toggle="tab"
                    aria-expanded="false"
                    class="nav-link"
                  >
                    <i class="mdi mdi-settings-outline d-md-none d-block"></i>
                    <span class="d-none d-md-block">Pending Approval</span>
                  </a>
                </li>
                <li class="nav-item" onClick={() => setFiltered("3")}>
                  <a
                    href="#settings-b1"
                    data-bs-toggle="tab"
                    aria-expanded="false"
                    class="nav-link"
                  >
                    <i class="mdi mdi-settings-outline d-md-none d-block"></i>
                    <span class="d-none d-md-block">Rejected</span>
                  </a>
                </li>
                <li class="nav-item" onClick={() => setFiltered("4")}>
                  <a
                    href="#settings-b1"
                    data-bs-toggle="tab"
                    aria-expanded="false"
                    class="nav-link"
                  >
                    <i class="mdi mdi-settings-outline d-md-none d-block"></i>
                    <span class="d-none d-md-block">Closed</span>
                  </a>
                </li>
              </ul>
            </div>

            <div className="d-flex justify-content-between align-items-center gap-3">
              <form
                onSubmit={handleSearchBy}
                className="d-flex bg-primary-1 align-items-center "
                style={{ height: "30px", width: "217px" }}
              >
                <input
                  type="search"
                  value={searchBy}
                  className="border-0"
                  placeholder="Search..."
                  onChange={(e) => setSearchBy(e.target.value)}
                  style={{
                    height: "28px",
                    marginLeft: "1px",
                    outline: "none",
                  }}
                />
                <button
                  type="submit"
                  className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                  style={{ height: "28px" }}
                >
                  <i className="uil uil-search text-white fs-4"></i>
                </button>
              </form>
              <div className=" d-flex align-items-center border ">
                <span
                  className="bg-info me-2 text-white"
                  style={{ padding: "3px" }}
                >
                  Export to
                </span>

                <span
                  href="#"
                  id="btnExport"
                  className="me-1 cursor-p"
                  title="Export to Pdf"
                  onClick={generatePDF}
                >
                  <img src={pdf} alt="export-pdf" style={{ height: "20px" }} />
                </span>
                {currentCustomers ? (
                  <CSVLink
                    data={exportXlsxData()}
                    style={{ fontWeight: "bolder", cursor: "pointer" }}
                    filename="Individual customers"
                  >
                    <span
                      href="#"
                      id="btnExport"
                      className="mr-1"
                      title="Export to Excel"
                    >
                      <img
                        src={excel}
                        alt="export-excel"
                        style={{ height: "20px", marginRight: "5px" }}
                      />
                    </span>
                  </CSVLink>
                ) : null}
              </div>
            </div>
          </div>
          <div className="col-12">
            <div className="position-relative">
              <div className="row">
                <div className="col-sm-12">
                  <div className="table-responsive">
                    <table
                      className="table table-centered table-striped nowrap w-100 no-footer"
                      ref={ICPdfRef}
                    >
                      <thead>
                        <tr>
                          <th
                            className="sorting"
                            tabIndex="0"
                            rowSpan="1"
                            colSpan="1"
                            style={{ width: "120.8px" }}
                            aria-label="Customer: activate to sort column ascending"
                          >
                            Status
                          </th>
                          <th
                            className="sorting"
                            tabIndex="0"
                            rowSpan="1"
                            colSpan="1"
                            style={{ width: "225.8px" }}
                            aria-label="Customer: activate to sort column ascending"
                          >
                            Names
                          </th>
                          <th
                            className="sorting"
                            tabIndex="0"
                            rowSpan="1"
                            colSpan="1"
                            style={{ width: "144.8px" }}
                            aria-label="Phone: activate to sort column ascending"
                          >
                            Gender
                          </th>
                          <th
                            className="sorting"
                            tabIndex="0"
                            rowSpan="1"
                            colSpan="1"
                            style={{ width: "147.8px" }}
                          >
                            Customer Type
                          </th>
                          <th
                            className="sorting"
                            tabIndex="0"
                            rowSpan="1"
                            colSpan="1"
                            style={{ width: "147.8px" }}
                            aria-label="Email: activate to sort column ascending"
                          >
                            Customer Id
                          </th>
                          <th
                            className="sorting"
                            tabIndex="0"
                            rowSpan="1"
                            colSpan="1"
                            style={{ width: "156.8px" }}
                            aria-label="Location: activate to sort column ascending"
                          >
                            State
                          </th>
                          <th
                            className="sorting sorting_asc"
                            tabIndex="0"
                            rowSpan="1"
                            colSpan="1"
                            style={{ width: "146.8px" }}
                          >
                            Phone Number
                          </th>
                          <th
                            className="sorting"
                            tabIndex="0"
                            rowSpan="1"
                            colSpan="1"
                            style={{ width: "165.8px" }}
                            aria-label="Status: activate to sort column ascending"
                          >
                            Date Registered
                          </th>
                          {viewCustomer && (
                            <th
                              tabIndex="0"
                              rowSpan="1"
                              colSpan="1"
                              style={{ width: "100.8px" }}
                            ></th>
                          )}
                        </tr>
                      </thead>

                      <tbody>
                        {currentCustomers?.map((item, i) => (
                          <tr key={i}>
                            <td>
                              <span
                                className={`badge ${
                                  item?.status === "1" &&
                                  "badge-warning-lighten"
                                } ${
                                  item?.status === "2" &&
                                  "badge-success-lighten"
                                } ${
                                  item?.status === "3" && "badge-danger-lighten"
                                } ${
                                  item?.status === "4" &&
                                  "badge-secondary-lighten"
                                } fw-normal rounded-4`}
                                style={{
                                  fontSize: "10px",
                                  padding: "5px 18px",
                                }}
                              >
                                {item?.status === "1" && "Pending Approval"}
                                {item?.status === "2" && "Active"}
                                {item?.status === "3" && "Rejected"}
                                {item?.status === "4" && "Closed"}
                              </span>
                            </td>
                            <td>{item?.first_name + " " + item?.last_name}</td>
                            <td className="table-user">{item?.gender}</td>
                            <td className="table-user">
                              {item?.customer_type}
                            </td>
                            <td className="text-dark">{item?.customer_id}</td>
                            <td>{item?.state_of_origin}</td>
                            <td>{item?.phone_number}</td>
                            <td>{moment(item?.created_at).format("D/M/Y")}</td>

                            {viewCustomer && (
                              <td className="text-center">
                                <Link
                                  to={`/admin/customer-profile/${item?.id}`}
                                  className="bg-primary-1 text-white px-2 py-1 rounded"
                                  state={item?.id}
                                >
                                  view
                                </Link>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {currentCustomers?.length < 1 ? (
                    <h5 className="alert alert-info">No result found!</h5>
                  ) : null}
                  <div className="row mt-2">
                    <div className="col-sm-12 col-md-6">
                      <div
                        className="dataTables_info table-pages"
                        id="products-datatable_info"
                        role="status"
                        aria-live="polite"
                      >
                        Showing offices {customers?.individual?.from} to{" "}
                        {customers?.individual?.to} of{" "}
                        {customers?.individual?.total}
                      </div>
                    </div>
                    <div className="col-sm-12 col-md-6">
                      <PaginationLinks
                        paginationData={customers?.individual}
                        getNextPage={fetchIndividualCustomersHandler}
                      />
                    </div>
                  </div>
                </div>
              </div>
              {(isFiltering || isSearching) && <Loading />}
            </div>
          </div>
          <CustomerType modal_id="create_customer_step_2" />
        </div>
      )}
    </>
  );
};

export default AllCustomer;
